import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  roleCanEditCatalog,
  type BinListResponse,
  type BinResponse,
  type PublicBin,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

export function BinsPage(): JSX.Element {
  const { user } = useAuth();
  const canEdit = user ? roleCanEditCatalog(user.role) : false;
  const [bins, setBins] = useState<PublicBin[]>([]);
  const [name, setName] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<BinListResponse>('/api/bins');
        if (!cancelled) setBins(body.bins);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load bins.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const body = await apiRequest<BinResponse>('/api/bins', {
        method: 'POST',
        body: { name, locationNote: locationNote || null },
      });
      setBins((current) => [...current, body.bin].sort((a, b) => a.name.localeCompare(b.name)));
      setName('');
      setLocationNote('');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the bin.');
    }
  }

  return (
    <section data-testid="bins-page">
      <div className="card">
        <h2>Shop bins</h2>
        <p className="meta">Shop storage. Consumable expected quantities can live on a bin.</p>
        {error ? <p className="status status--error">{error}</p> : null}
      </div>
      {canEdit ? (
        <form className="card" onSubmit={(event) => void onCreate(event)}>
          <h2>Add bin</h2>
          <label className="field">
            Name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              data-testid="bin-name"
            />
          </label>
          <label className="field">
            Location note
            <input value={locationNote} onChange={(event) => setLocationNote(event.target.value)} />
          </label>
          <button type="submit" className="button" data-testid="bin-create">
            Create bin
          </button>
        </form>
      ) : null}
      {loading ? <p className="meta">Loading bins…</p> : null}
      {bins.map((bin) => (
        <article className="card" key={bin.id}>
          <h2>{bin.name}</h2>
          <p className="meta">{bin.locationNote ?? (bin.active ? 'Active' : 'Inactive')}</p>
        </article>
      ))}
    </section>
  );
}
